import { createContext, useState, useEffect, useCallback } from 'react';
import { auth } from '../api/index.js';

/**
 * @typedef {Object} User
 * @property {string} id
 * @property {string} email
 * @property {string} [name]
 * @property {string} [avatarUrl]
 */

/**
 * @typedef {Object} AuthContextValue
 * @property {User | null} user
 * @property {boolean} isLoading
 * @property {boolean} isAuthenticated
 * @property {() => Promise<{success: boolean, error?: string}>} login
 * @property {() => Promise<void>} logout
 * @property {() => Promise<void>} refresh
 */

const AuthContext = createContext(/** @type {AuthContextValue | null} */ (null));

/**
 * AuthProvider component - provides authentication state to the app
 * @param {Object} props
 * @param {React.ReactNode} props.children
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(/** @type {User | null} */ (null));
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const userData = await auth.me();
      setUser(userData);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    async function checkAuth() {
      try {
        const userData = await auth.me();
        if (mounted) {
          setUser(userData);
        }
      } catch {
        if (mounted) {
          setUser(null);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    }

    checkAuth();

    return () => {
      mounted = false;
    };
  }, []);

  const login = useCallback(async () => {
    return await auth.login();
  }, []);

  const logout = useCallback(async () => {
    await auth.logout();
    setUser(null);
  }, []);

  const value = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    logout,
    refresh,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext;
